import { slugify } from '../utils/slugify';

export interface CvEntry {
  period: string;
  text: string;
}

export interface ExternalLink {
  text: string;
  label: string;
  href: string;
}

export interface ContentEntry {
  slug: string;
  title: string;
  description?: string;
  body?: string;
  seoTitle?: string;
  seoDescription?: string;
  email?: string;
  imageSrc?: string;
  imagePosition?: string;
  imagePositionMobile?: string;
  imageScale?: number;
  imageScaleMobile?: number;
  imageScaleDesktop?: number;
  imageTransformOrigin?: string;
  imagePositionDetail?: string;
  imageScaleDetail?: number;
  imageTransformOriginDetail?: string;
  quote?: string;
  cvEntries?: CvEntry[];
  publications?: string[];
  links?: ExternalLink[];
}

export const METHODIK_ITEMS: ContentEntry[] = [
  {
    slug: 'juristische-beratung-und-vertretung',
    title: 'Juristische Beratung und Vertretung',
    description:
      'Wir beraten Sie umfassend zu Ihren Rechten und Pflichten. Außergerichtlich wie vor Gericht vertreten wir Sie kompetent, klar und mit dem Blick auf das, was für Sie langfristig zählt.',
    seoTitle: 'Anwaltliche Beratung & Vertretung im Familienrecht | Zöllner Lorenz',
    seoDescription: 'Juristische Beratung und Vertretung im Familienrecht in München – kompetent, klar und mit Blick auf langfristige Lösungen.',
    body: `Eine fundierte rechtliche Einschätzung Ihrer Situation ist unerlässlich. Wir beraten Sie umfassend zu Ihren Rechten und Pflichten und zeigen Ihnen, welche Wege möglich sind. Dabei legen wir Wert auf Transparenz, damit Sie jederzeit wissen, wo Sie stehen.

Wir bemühen uns stets um eine einvernehmliche Lösung. Ist das nicht möglich, setzen wir Ihre Rechte auch vor Gericht entschlossen durch. Unser Ziel bleibt dabei ein Ergebnis, das auch langfristig trägt.

Sprechen Sie uns an – wir finden gemeinsam den richtigen Weg.`,
  },
  {
    slug: 'mediation',
    title: 'Mediation',
    description: 'Wir schaffen Raum für Gespräche, die allein nicht gelingen, und begleiten Sie zu einer Lösung, die beide Seiten tragen.',
    seoTitle: 'Mediation im Familienrecht in München | Zöllner Lorenz',
    seoDescription: 'Mediation bei Trennung und Scheidung in München – Konflikte lösen ohne Gericht, mit professioneller Begleitung und eigener Entscheidung.',
    body: `Mediation schafft Raum für Verständigung. Sie ist ein freiwilliges, außergerichtliches Verfahren zur Lösung von Konflikten. Die Beteiligten bleiben im direkten Gespräch und erarbeiten selbst Lösungen.

Als Mediatorinnen begleiten wir diesen Prozess allparteilich. Wir sorgen für eine strukturierte und respektvolle Gesprächsführung, klären Missverständnisse und machen die Interessen beider Seiten sichtbar.

Das Ziel ist eine Einigung, die zu beiden Seiten passt und die sie gemeinsam verantworten. Gemeinsam erarbeitete Lösungen werden erfahrungsgemäß langfristiger akzeptiert und verlässlicher umgesetzt als Entscheidungen, die von außen vorgegeben werden.

Mediation eignet sich besonders, wenn der Kontakt nach der Trennung weiterbesteht, zum Beispiel als gemeinsame Eltern. Eine erzielte Einigung lässt sich rechtlich verbindlich machen, etwa durch notarielle Beurkundung oder die Protokollierung im Scheidungstermin.

Haben Sie Interesse an einer Mediation? Sprechen Sie uns an.`,
  },
  {
    slug: 'cooperative-practice',
    title: 'Cooperative Praxis',
    description:
      'Gemeinsam an einem Tisch statt gegeneinander vor Gericht: Mit einer respektvollen, lösungsorientierten Verhandlungskultur begleiten wir Sie zu einer fairen Einigung.',
    seoTitle: 'Cooperative Praxis in München | Zöllner Lorenz',
    seoDescription: 'Cooperative Praxis (Collaborative Law) in München – Einigung ohne Gericht, mit eigenem anwaltlichem Beistand für jede Seite.',
    body: `Cooperative Praxis ist wie die Mediation ein außergerichtliches, interessenorientiertes Verfahren zur einvernehmlichen Konfliktlösung. International ist sie als Collaborative Practice oder Collaborative Law bekannt.

Anders als bei einer Mediation wird jede Seite von eigenen, speziell ausgebildeten anwaltlichen Vertretern begleitet. Alle Beteiligten sitzen gemeinsam an einem Tisch. Bei Bedarf kommen weitere Fachleute wie Coaches oder Expert/-innen für Kinder hinzu. Neben rechtlichen und wirtschaftlichen Fragen finden auch persönliche Anliegen ihren Platz.

Das Verfahren ist von gegenseitigem Respekt und dem gemeinsamen Willen zur Einigung geprägt. Die anwaltlichen Vertretungen beider Seiten arbeiten nicht gegeneinander, sondern gemeinsam an einer Lösung. Sie verpflichten sich, ihre Mandant/-innen nicht vor Gericht zu vertreten. Das unterstreicht den klaren Fokus auf eine außergerichtliche Klärung. Nur ein einvernehmliches Scheidungsverfahren dürfen sie auch vor Gericht begleiten.

Im Rahmen der Cooperative Praxis lässt sich ein Konflikt oft schneller und kostengünstiger lösen als vor Gericht. Die Verfahrensweise verbindet anwaltliche Sicherheit mit dem Wunsch nach einer fairen Einigung. Die Ergebnisse sind individuell und auf die Bedürfnisse aller Beteiligten zugeschnitten. So kann langfristiger Frieden wachsen.

Weitere Informationen finden Sie unter:`,
    links: [
      { text: 'MNCP, Münchner Netzwerk für Cooperative Praxis', label: 'www.cooperative-praxis.de', href: 'https://www.cooperative-praxis.de' },
      { text: 'DVCP, Deutsche Vereinigung für Cooperative Praxis', label: 'www.deutsche-vereinigung-cooperative-praxis.de', href: 'https://www.deutsche-vereinigung-cooperative-praxis.de' },
      { text: 'ENCP, European Network for Collaborative Practice', label: 'www.encp.eu', href: 'https://www.encp.eu' },
      { text: 'IACP, International Academy of Collaborative Professionals', label: 'www.collaborativepractice.com', href: 'https://www.collaborativepractice.com' },
    ],
  },
];

export const TEAM_INTRO =
  'Wir sind Marlene Zöllner und Susanna Lorenz – zwei Anwältinnen mit dem Ziel, Familienrecht menschlich, kompetent und lösungsorientiert zu gestalten.';

export const TEAM_MEMBERS: ContentEntry[] = [
  {
    slug: 'marlene-zoellner',
    title: 'Marlene Zöllner',
    description:
      'Partnerin · Rechtsanwältin · Fachanwältin für Familienrecht · Zertifizierte Mediatorin · Anwältin für Cooperative Praxis',
    email: 'zoellner@zoellner-lorenz.de',
    imageSrc: '/images/team-marlene.png',
    imagePosition: 'center center',
    seoTitle: 'Marlene Zöllner | Zöllner Lorenz',
    seoDescription:
      'Marlene Zöllner – Fachanwältin für Familienrecht, Mediatorin und Anwältin für Cooperative Praxis in München.',
    quote: 'Recht mit Haltung. Lösungen mit Herz.',
    cvEntries: [
      { period: '2026', text: 'Gründungspartnerin der Kanzlei Zöllner Lorenz' },
      { period: '2025', text: 'Fachanwältin für Familienrecht' },
      { period: '2024', text: 'Anwältin für Cooperative Praxis' },
      { period: '2023', text: 'Zertifizierte Mediatorin' },
      {
        period: 'seit 2021',
        text: 'Rechtsanwältin in einer renommierten Münchner Kanzlei für Familienrecht (Kanzlei Hartman-Hilter)',
      },
      {
        period: '2020',
        text: '2. Staatsexamen: Referendariat am Oberlandesgericht Düsseldorf mit Station u. a. beim Bundesministerium für Familie, Senioren, Frauen und Jugend',
      },
      {
        period: '2018',
        text: '1. Staatsexamen: Studium der Rechtswissenschaft an der Ludwig-Maximilians-Universität München und der Universität Helsinki',
      },
    ],
    publications: [
      'L. Bergschneider und B. Schramm (Hrsg.), Beck\'sches Formularbuch Familienrecht, C.H. Beck, München, 7. Auflage 2026 – Kapitel Mediation.',
    ],
  },
  {
    slug: 'susanna-lorenz',
    title: 'Susanna Lorenz',
    description:
      'Partnerin · Rechtsanwältin und ehemalige Staatsanwältin · Mediatorin · Anwältin für Cooperative Praxis',
    email: 'lorenz@zoellner-lorenz.de',
    imageSrc: '/images/team-susanna.png',
    imagePosition: 'center center',
    seoTitle: 'Susanna Lorenz | Zöllner Lorenz',
    seoDescription:
      'Susanna Lorenz – Rechtsanwältin, Mediatorin und Anwältin für Cooperative Praxis in München.',
    cvEntries: [
      { period: '2026', text: 'Gründungspartnerin der Kanzlei Zöllner Lorenz' },
      { period: '2026', text: 'Anwältin für Cooperative Praxis' },
      { period: '2026', text: 'Mediationsausbildung' },
      { period: '2026', text: 'Fachanwaltslehrgang Familienrecht' },
      {
        period: '2024–2026',
        text: 'Staatsanwältin (u. a. Verfolgung von häuslicher Gewalt)',
      },
      {
        period: '2020–2024',
        text: 'Rechtsanwältin bei führenden internationalen Wirtschaftskanzleien (Freshfields und Osborne Clarke)',
      },
      {
        period: '2020',
        text: '2. Staatsexamen: Referendariat am Oberlandesgericht Düsseldorf',
      },
      {
        period: '2018',
        text: '1. Staatsexamen: Studium der Rechtswissenschaft an der Ludwig-Maximilians-Universität München und der Universität Lissabon',
      },
    ],
  },
];

export const LEISTUNGEN_ITEMS: ContentEntry[] = [
  {
    slug: slugify('Ehevertrag und Scheidungsfolgenvereinbarung'),
    title: 'Ehevertrag und Scheidungsfolgenvereinbarung',
    seoTitle: 'Ehevertrag & Scheidungsfolgenvereinbarung in München | Zöllner Lorenz',
    seoDescription:
      'Ehevertrag und Scheidungsfolgenvereinbarung in München – vorausschauende Regelungen für Vermögen, Unterhalt und Versorgungsausgleich.',
    body: `Klare Vereinbarungen schaffen Sicherheit. Am besten, bevor es zum Streit kommt. Ein Ehevertrag regelt vorausschauend, was im Fall der Fälle gelten soll: etwa Vermögensschutz, Versorgungsausgleich und Unterhalt. Steht die Trennung bereits fest, bündelt eine Scheidungsfolgenvereinbarung die offenen Punkte in einer einvernehmlichen Lösung.

Gerade bei Unternehmensbeteiligungen, Immobilien oder Schenkungen und Erbschaften ist eine maßgeschneiderte Regelung oft entscheidend.

Wir denken mit Ihnen voraus und entwickeln eine Vereinbarung, die zu Ihrer Lebenssituation passt und im Ernstfall auch hält. Von der ersten Beratung bis zur notariellen Beurkundung oder Protokollierung im Scheidungstermin. Fair für beide Seiten und mit Blick auf das, was langfristig trägt.`,
  },
  {
    slug: slugify('Trennung und Scheidung'),
    title: 'Trennung und Scheidung',
    seoTitle: 'Trennung und Scheidung in München | Zöllner Lorenz',
    seoDescription:
      'Trennung und Scheidung in München – wir begleiten Sie kompetent und lösungsorientiert durch alle rechtlichen Schritte.',
    body: `Eine Trennung ordnet häufig das gesamte Leben neu – emotional, finanziell und rechtlich. Wir begleiten Sie durch alle rechtlichen Schritte, vom Trennungsjahr über den Scheidungsantrag bis zu allen damit verbundenen Folgen. Dabei stehen wir Ihnen auch menschlich zur Seite.

Im Blick behalten wir nicht nur das Verfahren, sondern auch das, was danach kommt. Damit Sie und Ihre Kinder gut in den nächsten Lebensabschnitt starten können. Das gilt für verheiratete und unverheiratete Paare gleichermaßen.

Je nach Situation kommen unterschiedliche Wege in Betracht: Mediation, Cooperative Praxis oder die anwaltliche Begleitung und Vertretung. Gemeinsam besprechen wir, welcher Weg für Sie der richtige ist.`,
  },
  {
    slug: slugify('Umgang und Sorgerecht'),
    title: 'Umgang und Sorgerecht',
    seoTitle: 'Sorgerecht & Umgangsrecht in München | Zöllner Lorenz',
    seoDescription:
      'Umgang und Sorgerecht in München – wir begleiten Sie zu verlässlichen Regelungen im Sinne des Kindeswohls.',
    body: `Wenn Eltern sich trennen, stellt sich häufig eine zentrale Frage: Wie können die Kinder bestmöglich begleitet und geschützt werden?

Das Sorgerecht betrifft die wesentlichen Entscheidungen im Leben des Kindes. Beim Umgangsrecht geht es um die gelebte Zeit mit beiden Elternteilen. Maßstab ist immer das Wohl des Kindes.

Wir setzen auf Lösungen, die das Kind aus dem Konflikt der Eltern heraushalten – durch klare, verlässliche Absprachen. Unser Ziel ist eine einvernehmliche Regelung, die beiden Elternteilen gerecht wird und dem Kind die Stabilität schenkt, die es braucht, um unbeschwert aufwachsen zu können.`,
  },
  {
    slug: slugify('Vermögensauseinandersetzung und Zugewinnausgleich'),
    title: 'Vermögensauseinandersetzung und Zugewinnausgleich',
    seoTitle: 'Vermögensauseinandersetzung in München | Zöllner Lorenz',
    seoDescription:
      'Vermögensauseinandersetzung und Zugewinnausgleich in München – auch bei Unternehmen, Immobilien und komplexen Vermögensstrukturen.',
    body: `Im gesetzlichen Güterstand wird der während der Ehe erwirtschaftete Vermögenszuwachs zwischen den Eheleuten ausgeglichen. Das ist der Zugewinnausgleich. Bei größeren oder komplexen Vermögen wird das schnell anspruchsvoll. Unternehmensbeteiligungen, Immobilien und Kapitalanlagen sowie ihre Bewertung verlangen Sorgfalt und Erfahrung.

Haben Sie in einem Ehevertrag eine abweichende Regelung getroffen, etwa Gütertrennung oder eine modifizierte Zugewinngemeinschaft, prüfen wir die Wirksamkeit des Vertrages und was das konkret für Sie bedeutet.

Neben dem Zugewinnausgleich stellen sich bei einer Trennung oft weitere Vermögensfragen, etwa der Umgang mit gemeinsamen Immobilien, häufig dem Familienheim, oder die Aufteilung gemeinsamer Konten. Auch dabei begleiten wir Sie.

Wir verschaffen Ihnen zunächst Klarheit über die Vermögenslage, prüfen Bewertungsfragen und begleiten Sie zu einer Lösung, die langfristig trägt. Einvernehmlich, wo immer das möglich ist.`,
  },
  {
    slug: slugify('Unternehmen in Trennung und Scheidung'),
    title: 'Unternehmen in Trennung und Scheidung',
    seoTitle: 'Unternehmen in Trennung und Scheidung in München | Zöllner Lorenz',
    seoDescription:
      'Unternehmensbeteiligung bei Trennung und Scheidung in München – Zugewinnausgleich, Unterhalt und Ehevertrag mit Blick auf ein handlungsfähiges Unternehmen.',
    body: `Ist ein Ehegatte an einem Unternehmen oder einer Praxis beteiligt, wirkt sich das auf fast alle Scheidungsfolgen aus. Im Rahmen des Zugewinnausgleichs wird das Unternehmen nicht geteilt. Sein Wertzuwachs während der Ehe fließt aber in die Berechnung mit ein.

Wir klären, was die Beteiligung für Zugewinnausgleich und Unterhalt bedeutet. Außerdem zeigen wir Ihnen, wie sich das vorab im Ehevertrag regeln lässt. Ziel ist eine Lösung, die Ihr Unternehmen auch langfristig handlungsfähig hält.`,
  },
  {
    slug: slugify('Unterhalt'),
    title: 'Unterhalt',
    seoTitle: 'Unterhalt in München | Zöllner Lorenz',
    seoDescription:
      'Kindesunterhalt, Trennungs- und nachehelicher Unterhalt, Betreuungsunterhalt in München – wir begleiten Sie zu einer tragfähigen und möglichst einvernehmlichen Lösung.',
    body: `Unterhalt ist eine zentrale Frage bei fast jeder Trennung. Das Unterhaltsrecht umfasst verschiedene Bereiche – etwa Kindesunterhalt, Trennungsunterhalt, nachehelicher Unterhalt oder Betreuungsunterhalt.

Jede Unterhaltsart folgt eigenen Regeln und Maßstäben und jede Situation hat ihre eigenen Besonderheiten. Wir berechnen Ihre Ansprüche oder Verpflichtungen nachvollziehbar, ordnen sie realistisch ein und begleiten Sie zu einer tragfähigen Lösung – idealerweise einvernehmlich.`,
  },
  {
    slug: slugify('Versorgungsausgleich'),
    title: 'Versorgungsausgleich',
    seoTitle: 'Versorgungsausgleich in München | Zöllner Lorenz',
    seoDescription:
      'Versorgungsausgleich bei Scheidung in München – wir sichern Ihre Altersversorgung und prüfen Gestaltungsmöglichkeiten.',
    body: `Im Versorgungsausgleich werden die während der Ehezeit erworbenen Rentenanwartschaften zwischen den Eheleuten geteilt. Was technisch klingt, hat erhebliche Auswirkungen auf die Absicherung im Alter.

Wir prüfen die einbezogenen Anrechte und erläutern Ihnen verständlich, was der Versorgungsausgleich für Ihre Altersvorsorge bedeutet.`,
  },
  {
    slug: slugify('Ehewohnung und Hausrat'),
    title: 'Ehewohnung und Hausrat',
    seoTitle: 'Ehewohnung und Hausrat in München | Zöllner Lorenz',
    seoDescription:
      'Zuweisung der Ehewohnung und Aufteilung des Hausrats bei Trennung und Scheidung in München – einvernehmlich, wo möglich, und mit Blick auf das, was die Situation erfordert.',
    body: `Wer bleibt in der gemeinsamen Wohnung, wer zieht aus und wie wird der Hausrat aufgeteilt? Diese Fragen müssen bei einer Trennung praktisch geklärt werden, und sie sind selten nur sachlich.

Wir klären, welche Regelung für die Übergangszeit und für die Zeit nach der Scheidung sinnvoll ist, und begleiten Sie zu einer Lösung, die für alle Beteiligten tragbar ist – möglichst einvernehmlich und mit Blick auf das, was die Situation erfordert.`,
  },
  {
    slug: slugify('Namensrecht'),
    title: 'Namensrecht',
    seoTitle: 'Namensrecht in München | Zöllner Lorenz',
    seoDescription:
      'Namensrecht bei Eheschließung und Scheidung in München – Namensführung und Kindesname verständlich erklärt und begleitet.',
    body: `Mit Eheschließung, Trennung und Scheidung stellen sich Namensfragen – für Sie selbst und für Ihre Kinder. Wir erklären Ihnen Ihre Möglichkeiten und begleiten Sie durch die nötigen Schritte.`,
  },
  {
    slug: slugify('Adoption'),
    title: 'Adoption',
    seoTitle: 'Adoption in München | Zöllner Lorenz',
    seoDescription:
      'Adoption in München – Stiefkind-, Minderjährigen- und Erwachsenenadoption, verständlich beraten und einfühlsam begleitet.',
    body: `Eine Adoption begründet rechtlich eine neue Familiengemeinschaft. Stiefkindadoption, Kindesadoption und Erwachsenenadoption folgen jeweils eigenen Voraussetzungen und haben unterschiedliche rechtliche Wirkungen.

Wir erklären Ihnen verständlich, welche Form der Adoption zu Ihrer Situation passt, und begleiten Sie mit Sorgfalt und Blick auf alle Beteiligten durch das gesamte Verfahren.`,
  },
];

export const AKTUELLES_ITEMS: ContentEntry[] = [
  {
    slug: slugify('Unternehmen in der Scheidung'),
    title: 'Unternehmen in der Scheidung',
    description:
      'Worauf Inhaber/-innen beim Zugewinnausgleich achten sollten – und wie sich das Unternehmen aus dem Konflikt heraushalten lässt.',
  },
  {
    slug: slugify('Ehevertrag – klug statt unromantisch'),
    title: 'Ehevertrag – klug statt unromantisch',
    description:
      'Wann ein Ehevertrag sinnvoll ist, was er regeln kann und wie er beiden Seiten Sicherheit gibt.',
  },
  {
    slug: slugify('Kindschaftsrechtsreform 2026'),
    title: 'Kindschaftsrechtsreform 2026: Wird das Wechselmodell zum Regelfall?',
    description:
      'Der neue Gesetzentwurf benennt erstmals alle Betreuungsmodelle gleichrangig – ohne einem den Vorrang einzuräumen. Was das für getrennte Eltern wirklich bedeutet.',
  },
  {
    slug: slugify('Mediation im Familienrecht'),
    title: 'Mediation im Familienrecht',
    description:
      'Warum sich immer mehr Paare für den gemeinsamen Weg statt für den Streit entscheiden.',
  },
  {
    slug: slugify('Cooperative Praxis'),
    title: 'Cooperative Praxis',
    description:
      'Der kooperative Weg durch die Trennung – und seine Vorteile gegenüber dem klassischen streitigen Verfahren.',
  },
];

export const ENTWICKLUNGEN_INITIAL_VISIBLE = 3;

export function findBySlug(items: ContentEntry[], slug: string | undefined) {
  if (!slug) return undefined;
  return items.find((item) => item.slug === slug);
}
